import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { consultationDefaults } from '../../assets/consultationOptions';
import {
  clearConsultationDraft,
  loadConsultationDraft,
  saveConsultationDraft
} from '../../lib/consultationDraft';
import { consultationSchema, stepFields, stepSchemas } from '../../lib/consultationSchema';
import { handleConsultationSubmit } from '../../lib/consultationSubmit';
import { Button } from '../Button';
import { StepContactInfo } from './StepContactInfo';
import { StepMeetingSchedule } from './StepMeetingSchedule';
import { StepProgress } from './StepProgress';
import { StepProjectDetails } from './StepProjectDetails';
import { StepReview } from './StepReview';
import { StepSuccess } from './StepSuccess';

const stepMotion = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.28, ease: 'easeOut' }
};

export function ConsultationWizard() {
  const draftValues = useMemo(() => loadConsultationDraft(), []);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submittedPayload, setSubmittedPayload] = useState(null);

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    getValues,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(consultationSchema),
    defaultValues: draftValues,
    mode: 'onBlur'
  });

  const values = watch();

  useEffect(() => {
    if (step >= 5) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      saveConsultationDraft(getValues());
    }, 350);

    return () => window.clearTimeout(timer);
  }, [values, step, getValues]);

  const goNext = async () => {
    setSubmitError('');
    const fields = stepFields[step] || [];
    const valid = fields.length ? await trigger(fields) : true;

    if (!valid) {
      return;
    }

    // Extra per-step schema check keeps partial drafts honest.
    const parsed = stepSchemas[step]?.safeParse(getValues());
    if (parsed && !parsed.success) {
      await trigger(fields);
      return;
    }

    setStep((current) => Math.min(current + 1, 5));
  };

  const goBack = () => {
    setSubmitError('');
    setStep((current) => Math.max(current - 1, 1));
  };

  const onSubmit = handleSubmit(async (data) => {
    setSubmitting(true);
    setSubmitError('');

    try {
      const payload = await handleConsultationSubmit(data);
      clearConsultationDraft();
      setSubmittedPayload(payload);
      setStep(5);
    } catch (error) {
      setSubmitError(error?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="booking-shell glass-panel">
      <StepProgress currentStep={step} />

      <form className="booking-form" onSubmit={onSubmit} noValidate>
        <AnimatePresence mode="wait">
          <motion.div key={step} {...stepMotion}>
            {step === 1 ? <StepProjectDetails register={register} errors={errors} /> : null}
            {step === 2 ? <StepContactInfo register={register} errors={errors} /> : null}
            {step === 3 ? <StepMeetingSchedule register={register} errors={errors} /> : null}
            {step === 4 ? <StepReview values={getValues()} /> : null}
            {step === 5 ? <StepSuccess summary={submittedPayload || consultationDefaults} /> : null}
          </motion.div>
        </AnimatePresence>

        {submitError ? (
          <p className="booking-form__error" role="alert">
            {submitError}
          </p>
        ) : null}

        {step < 5 ? (
          <div className="booking-actions">
            {step > 1 ? (
              <Button type="button" variant="secondary" onClick={goBack} disabled={submitting}>
                Back
              </Button>
            ) : (
              <span />
            )}

            {step < 4 ? (
              <Button type="button" onClick={goNext}>
                Continue
              </Button>
            ) : (
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Consultation'}
              </Button>
            )}
          </div>
        ) : null}

        {step < 5 ? (
          <p className="booking-form__draft">Draft auto-saves in this browser while you type.</p>
        ) : null}
      </form>
    </div>
  );
}
